import { useCallback, useContext, useEffect } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { SocketContext } from '@/contexts/rtm';
import { selectUserId } from '@/redux/user/selectors';
import type { Comment } from '@/shared/types/comments';
import { CommentEventId, SocketEvent, UserEvent } from '@/shared/types/rtm';

import { commentKeys, onCommentCreated, onCommentRemoved } from './useComments';

const useRtmComments = (deskId: string, sessionId: string) => {
  const { socket } = useContext(SocketContext);
  const queryClient = useQueryClient();
  const userId = useSelector(selectUserId);

  const handleCreated = useCallback<UserEvent<Comment & { account_id: string }>>(
    ({ user_id, data }) => {
      if (user_id === userId || data.session_id !== sessionId) {
        return;
      }
      onCommentCreated(queryClient, deskId, data);
    },
    [deskId, queryClient, sessionId, userId]
  );

  const handleUpdated = useCallback<UserEvent<CommentEventId>>(
    ({ user_id, data }) => {
      if (data.session_id !== sessionId) {
        return;
      }
      if (user_id === userId) {
        return;
      }
      queryClient.invalidateQueries({ queryKey: commentKeys.list(deskId, sessionId) });
    },
    [deskId, queryClient, sessionId, userId]
  );

  const handleRemoved = useCallback<UserEvent<CommentEventId>>(
    ({ user_id, data }) => {
      if (data.session_id !== sessionId) {
        return;
      }
      if (user_id === userId) {
        return;
      }
      onCommentRemoved(queryClient, deskId, sessionId, data.comment_id);
    },
    [deskId, queryClient, sessionId, userId]
  );

  useEffect(() => {
    socket?.on(SocketEvent.comments_created, handleCreated);
    socket?.on(SocketEvent.comments_updated, handleUpdated);
    socket?.on(SocketEvent.comments_removed, handleRemoved);
    return () => {
      socket?.off(SocketEvent.comments_created, handleCreated);
      socket?.off(SocketEvent.comments_updated, handleUpdated);
      socket?.off(SocketEvent.comments_removed, handleRemoved);
    };
  }, [handleCreated, handleUpdated, handleRemoved, socket]);
};

export default useRtmComments;
